import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { t } from '@/i18n'
import type { AuthUser } from '@/nordy/interfaces'
import { registerMixpanelLoggedInUser } from '@/nordy/utils/mixpanelUserProperties'
import { isValidNordyUserId } from '@/nordy/utils/nordyUserId'
import { pushNordyUserContext } from '@/nordy/utils/gtm'
import { useToastStore } from '@/platform/updates/common/toastStore'
import { UserRole } from '@/nordy/interfaces'
import { nordyApi } from '@/nordy/api'
import { api } from '@/scripts/api'
import { Gtag } from '../utils/gtag'

export interface User {
  userId: string
  username: string
}

export const useNordyUserStore = defineStore('nordyUser', () => {
  /** =======================================================
   * !nordy - 로그인 관련 로직
   * ========================================================= */
  const nordyUser = ref<AuthUser | null>(null)
  const isUserLoaded = ref(false)
  let isUserResolved = false
  let userLoadPromise: Promise<AuthUser | null> | null = null

  const credit = ref({
    totalAmount: 0,
    onlyFree: true
  })
  const isCreditLoaded = ref(false)

  function resetUserState(): void {
    nordyUser.value = null
    isCreditLoaded.value = false
    credit.value = {
      totalAmount: 0,
      onlyFree: true
    }
  }

  //!nordy - header와 동일하게 subscriptionItemName으로 현재 플랜을 판정한다.
  // subscriptionItemKind는 결제수단 denorm 값이므로 구독 취소 후 stale할 수 있다.
  const isPaymentSubscriptionUser = computed(() => {
    const planName = nordyUser.value?.subscriptionItemName ?? 'Free'
    return planName !== 'Free'
  })

  const isAdminUser = computed(() => {
    return (
      nordyUser.value?.roles?.some((role) => role === UserRole.Admin) ?? false
    )
  })

  async function handleInvalidUserIdentity(): Promise<void> {
    resetUserState()
    useToastStore().add({
      severity: 'error',
      summary: t('g.error'),
      detail: t('auth.login.invalidSession'),
      life: 5000
    })
    const { useNordyModalStore } = await import('@/nordy/store/nordyModalStore')
    useNordyModalStore().openLoginModal()
  }

  function getOrStartUserLoad(): Promise<AuthUser | null> {
    if (userLoadPromise) return userLoadPromise

    isUserLoaded.value = false
    isUserResolved = false

    userLoadPromise = (async () => {
      const data = await nordyApi.getUser()
      if (!data) {
        resetUserState()
        return null
      }
      if (!isValidNordyUserId(data._id)) {
        await handleInvalidUserIdentity()
        return null
      }
      Gtag.comfyui_login({
        user_id: data._id,
        price_type: data.price_type ?? null
      })
      if (nordyUser.value?._id !== data._id) isCreditLoaded.value = false
      nordyUser.value = data
      pushNordyUserContext(data)
      registerMixpanelLoggedInUser(data)
      api.setNordyApiKey(data.apiKey)
      //!nordy - 쿠폰 구독 만료 후 첫 접속(서버가 1회성으로 내려줌)이면 결제유도 모달 노출.
      // 동적 import로 순환참조 방지(nordyModalStore → 모달 컴포넌트 → nordyUserStore)
      if (data.couponExpiredModal) {
        const { useNordyModalStore } =
          await import('@/nordy/store/nordyModalStore')
        useNordyModalStore().openCouponExpiredModal()
      }
      return nordyUser.value
    })()

    const request = userLoadPromise
    void request.then(
      () => {
        if (userLoadPromise !== request) return
        isUserLoaded.value = true
        isUserResolved = true
        userLoadPromise = null
      },
      (error) => {
        if (userLoadPromise !== request) return
        console.error('nordyUser 불러오기 실패:', error)
        isUserLoaded.value = true
        userLoadPromise = null
      }
    )

    return request
  }

  async function loadNordyUser(): Promise<AuthUser | null> {
    try {
      return await getOrStartUserLoad()
    } catch {
      return null
    }
  }

  async function reloadNordyUser(): Promise<AuthUser | null> {
    if (userLoadPromise) {
      try {
        await userLoadPromise
      } catch {
        // The fresh request below determines the post-login session.
      }
    }

    return loadNordyUser()
  }

  function ensureUserLoaded(): Promise<AuthUser | null> {
    if (isUserResolved) return Promise.resolve(nordyUser.value)

    return getOrStartUserLoad()
  }

  //!nordy - 성공 여부를 반환하여 호출부에서 재시도 판단 가능
  async function getActiveCredit(): Promise<{ success: boolean }> {
    if (!isUserResolved || !nordyUser.value) {
      return { success: false }
    }

    const userId = nordyUser.value._id
    try {
      const data = await nordyApi.getActiveCredit()
      if (nordyUser.value?._id !== userId) return { success: false }
      credit.value = data
      isCreditLoaded.value = true
      return { success: true }
    } catch (error) {
      console.error('Active credit 요청 실패:', error)
      return { success: false }
    }
  }

  return {
    getActiveCredit,
    loadNordyUser,
    reloadNordyUser,
    ensureUserLoaded,
    isUserLoaded,
    isCreditLoaded,
    credit,
    nordyUser,
    isPaymentSubscriptionUser,
    isAdminUser
  }
})
